import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import type { Reply } from '@olimhub/shared'
import type { Language } from '@olimhub/shared'
import { postsApi } from '@/api'
import { ReplyCard } from './ReplyCard'
import { ReplyComposer } from './ReplyComposer'

interface ReplyListProps {
  postId: string
  parentUsername: string
  replyCount: number
  postLanguage?: Language
}

export function ReplyList({ postId, parentUsername, replyCount, postLanguage }: ReplyListProps) {
  const { t } = useTranslation()
  const [localReplies, setLocalReplies] = useState<Reply[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)

  const { data, isFetching } = useQuery({
    queryKey: ['replies', postId, cursor],
    queryFn: () => postsApi.getReplies(postId, cursor),
    staleTime: 30_000,
  })

  const fetchedReplies = data?.items ?? []
  const hasMore = data?.hasMore ?? false
  const nextCursor = data?.nextCursor ?? undefined

  const allReplies = [
    ...fetchedReplies,
    ...localReplies.filter((lr) => !fetchedReplies.some((fr) => fr.id === lr.id)),
  ]

  const handleReplyCreated = (reply: Reply) => {
    setLocalReplies((prev) => [...prev, reply])
  }

  const handleDeleted = (id: string) => {
    setLocalReplies((prev) => prev.filter((r) => r.id !== id))
  }

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        background: 'var(--surface)',
        padding: '12px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      {isFetching && allReplies.length === 0 ? (
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-xs)',
            color: 'var(--text-faint)',
            padding: '4px 0',
          }}
        >
          {t('post.loading_replies')}
        </div>
      ) : (
        <>
          {allReplies.map((reply) => (
            <ReplyCard
              key={reply.id}
              reply={reply}
              onDeleted={handleDeleted}
            />
          ))}

          {hasMore && nextCursor && (
            <button
              type="button"
              onClick={() => setCursor(nextCursor)}
              disabled={isFetching}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--fs-sm)',
                color: 'var(--brand)',
                fontWeight: 600,
                padding: '2px 0',
              }}
            >
              <ChevronDown size={14} />
              {t('post.see_more_replies', { count: replyCount - allReplies.length })}
            </button>
          )}
        </>
      )}

      <ReplyComposer
        postId={postId}
        parentUsername={parentUsername}
        defaultLanguage={postLanguage}
        onReplyCreated={handleReplyCreated}
      />
    </div>
  )
}
